import { useState } from "react";
import { Server, Settings2, Workflow, LifeBuoy, ArrowUpRight, Layers, RefreshCw, Plug, LayoutGrid, Headphones, Eye } from "lucide-react";
import { Reveal, Eyebrow, useInView } from "./Reveal";

/* 3 — The invisible infrastructure */
export function InvisibleInfrastructure() {
  const [ref, inView] = useInView({ threshold: 0.3 });
  const words = "The technology you don't see.".split(" ");
  return (
    <section className="relative max-w-6xl mx-auto px-5 pt-24 md:pt-36 pb-10" data-testid="invisible-infra-section">
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-14 items-center">
        <div ref={ref}>
          <Reveal><Eyebrow>Built behind the experience</Eyebrow></Reveal>
          <h2 className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-5">
            {words.map((w, i) => (
              <span key={i}><span className={`word ${inView ? "word-in" : ""}`} style={{ transitionDelay: `${i * 90}ms` }}>{w}</span>{" "}</span>
            ))}
            <br />
            <span className={`word text-[#67F2B3] ${inView ? "word-in" : ""}`} style={{ transitionDelay: `${words.length * 90 + 120}ms` }}>
              The reliability you experience.
            </span>
          </h2>
          <Reveal delay={300}>
            <p className="mt-7 text-base md:text-lg text-[#F4FFF9]/60 max-w-xl leading-relaxed">
              Behind every reliable digital experience is an architecture designed to keep systems connected, services organized, and businesses moving forward.
            </p>
          </Reveal>
        </div>
        <ArchitectureLines active={inView} />
      </div>
    </section>
  );
}

const NODES = [[60, 60], [300, 40], [180, 160], [40, 260], [320, 250], [190, 330]];
const LINKS = [[0, 2], [1, 2], [2, 3], [2, 4], [3, 5], [4, 5], [0, 1]];

function ArchitectureLines({ active }) {
  return (
    <svg viewBox="0 0 360 380" className={`w-full max-w-sm mx-auto arch ${active ? "arch-in" : ""}`} aria-hidden data-testid="architecture-diagram">
      {LINKS.map(([a, b], i) => {
        const [x1, y1] = NODES[a], [x2, y2] = NODES[b];
        const d = `M${x1} ${y1} C ${(x1 + x2) / 2} ${y1}, ${(x1 + x2) / 2} ${y2}, ${x2} ${y2}`;
        return (
          <g key={i}>
            <path d={d} className="arch-path" style={{ transitionDelay: `${i * 140}ms` }} />
            <path d={d} className="arch-pulse" style={{ animationDelay: `${i * 0.9}s` }} />
          </g>
        );
      })}
      {NODES.map(([x, y], i) => (
        <g key={i} className="arch-node" style={{ transitionDelay: `${300 + i * 120}ms` }}>
          <rect x={x - 14} y={y - 14} width="28" height="28" rx="7" />
          <circle cx={x} cy={y} r="3" />
        </g>
      ))}
    </svg>
  );
}

/* 5 — Connected by design (services) */
const SERVICES = [
  { icon: Server, title: "Digital Infrastructure", desc: "Website environments, hosting-related coordination, technical configurations and digital foundation management." },
  { icon: Settings2, title: "Managed Services", desc: "Ongoing service coordination, maintenance workflows, technical assistance and operational continuity." },
  { icon: Workflow, title: "System Integrations", desc: "Connection and configuration of compatible third-party platforms and business systems." },
  { icon: LifeBuoy, title: "Technical Support", desc: "Troubleshooting, maintenance assistance and technical service requests." },
];

export function ConnectedServices({ onRequest }) {
  return (
    <section id="services" className="relative max-w-6xl mx-auto px-5 py-24 md:py-32" data-testid="services-section">
      <Reveal className="max-w-2xl">
        <Eyebrow>What we do</Eyebrow>
        <h2 className="font-heading font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-5">
          Connected by design.<br /><span className="text-[#F4FFF9]/45">Managed with intention.</span>
        </h2>
      </Reveal>
      <div className="mt-14 grid sm:grid-cols-2 border-t border-l border-white/[0.06]">
        {SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={i * 90} data-testid={`service-card-${s.title.toLowerCase().replace(/\s/g, "-")}`}
            className="svc-card group border-r border-b border-white/[0.06] p-7 md:p-10">
            <div className="flex items-start justify-between">
              <div className="svc-icon"><s.icon className="h-5 w-5 text-[#67F2B3]" strokeWidth={1.5} /></div>
              <span className="font-mono text-[11px] text-[#F4FFF9]/25">0{i + 1}</span>
            </div>
            <h3 className="font-heading font-semibold text-xl md:text-2xl mt-8">{s.title}</h3>
            <p className="text-sm md:text-base text-[#F4FFF9]/55 mt-3 leading-relaxed max-w-sm">{s.desc}</p>
            <button onClick={onRequest} data-testid={`service-request-${i}`}
              className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#67F2B3]/80 hover:text-[#67F2B3] transition-colors">
              Request this service <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* 6 — From complexity to clarity */
const PROCESS = [
  ["Define", "Identify the service and technical requirements."],
  ["Configure", "Structure the service and its ongoing management."],
  ["Connect", "Coordinate the required technologies and workflows."],
  ["Maintain", "Provide ongoing service administration and support within the agreed scope."],
];

function ProcessStep({ i, title, desc }) {
  const [ref, on] = useInView({ threshold: 0.6 });
  return (
    <div ref={ref} className={`proc-step ${on ? "proc-on" : ""}`} style={{ transitionDelay: `${i * 160}ms` }} data-testid={`process-step-${i + 1}`}>
      <div className="proc-node"><span className="font-mono text-xs">0{i + 1}</span></div>
      <div className="lg:mt-6">
        <h3 className="font-heading font-semibold text-xl">{title}</h3>
        <p className="text-sm text-[#F4FFF9]/55 mt-2 leading-relaxed max-w-[240px]">{desc}</p>
      </div>
    </div>
  );
}

export function ProcessDiagram() {
  return (
    <section className="relative max-w-6xl mx-auto px-5 py-24 md:py-32" data-testid="process-section">
      <Reveal className="max-w-2xl">
        <Eyebrow>How ECOREXS operates</Eyebrow>
        <h2 className="font-heading font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-5">
          From complexity<br /><span className="text-[#67F2B3]">to clarity.</span>
        </h2>
      </Reveal>
      <div className="proc-track mt-16 grid lg:grid-cols-4 gap-10 lg:gap-6">
        {PROCESS.map(([t, d], i) => <ProcessStep key={t} i={i} title={t} desc={d} />)}
      </div>
    </section>
  );
}

/* 7 — Platform concept preview */
const PREVIEW_SERVICES = [
  { name: "Website Environment", cat: "Digital Infrastructure", status: "Managed" },
  { name: "Maintenance Workflow", cat: "Managed Services", status: "Active" },
  { name: "Payments Integration", cat: "System Integrations", status: "Configuring" },
];

export function PlatformPreview() {
  const [cycle, setCycle] = useState("monthly");
  return (
    <section className="relative max-w-6xl mx-auto px-5 py-24 md:py-32" data-testid="platform-preview-section">
      <div className="grid lg:grid-cols-[1fr_1.35fr] gap-12 items-center">
        <Reveal>
          <Eyebrow>Managed digital services</Eyebrow>
          <h2 className="font-heading font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-5">
            Every service.<br /><span className="text-[#F4FFF9]/45">One organized view.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#F4FFF9]/60 leading-relaxed max-w-md">
            Services are organized per client, with clear categories, status and billing cycle — including annual billing when it is offered.
          </p>
        </Reveal>
        <Reveal delay={150}>
          <div className="glass-panel overflow-hidden" data-testid="platform-preview-card">
            <div className="flex items-center justify-between px-5 h-11 border-b border-white/5">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#F4FFF9]/45">Platform Concept Preview</span>
              <span className="font-mono text-[10px] text-[#F4FFF9]/30">sample data</span>
            </div>
            <div className="p-5 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs text-[#F4FFF9]/40">Client overview</div>
                  <div className="font-heading font-semibold text-lg">Example Company</div>
                </div>
                <div className="flex p-1 rounded-full bg-white/[0.04] border border-white/10" role="radiogroup" data-testid="preview-billing-toggle">
                  {["monthly", "annual"].map((c) => (
                    <button key={c} role="radio" aria-checked={cycle === c} onClick={() => setCycle(c)} data-testid={`preview-cycle-${c}`}
                      className={`px-3 py-1 rounded-full text-xs capitalize transition-colors ${cycle === c ? "bg-[#67F2B3] text-[#071A13] font-semibold" : "text-[#F4FFF9]/60 hover:text-[#F4FFF9]"}`}>
                      {c}{c === "annual" && <span className={cycle === c ? "" : "text-[#67F2B3]"}> · −20%</span>}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-5 space-y-2.5">
                {PREVIEW_SERVICES.map((s) => (
                  <div key={s.name} className="preview-row">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{s.name}</div>
                      <div className="text-[11px] text-[#F4FFF9]/40 truncate">{s.cat}<span className="sm:hidden text-[#67F2B3]/70"> · {cycle === "annual" ? "Annual" : "Monthly"}</span></div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="pill-muted hidden sm:inline">{cycle === "annual" ? "Billed annually" : "Billed monthly"}</span>
                      <span className={`pill-status ${s.status === "Configuring" ? "pill-wait" : ""}`}>{s.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* 8 — Built for long-term operations */
const LONG = [
  [Layers, "Organized technical environments"],
  [RefreshCw, "Managed service continuity"],
  [Plug, "Integration-friendly architecture"],
  [LayoutGrid, "Centralized service coordination"],
  [Headphones, "Support-oriented workflows"],
  [Eye, "Clearer operational visibility"],
];

export function LongTerm() {
  return (
    <section className="relative border-y border-white/[0.06] bg-[#030B08]/50" data-testid="long-term-section">
      <div className="max-w-6xl mx-auto px-5 py-24 md:py-32 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <Eyebrow>Built for long-term operations</Eyebrow>
          <h2 className="font-heading font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-5">
            Your business evolves.<br /><span className="text-[#67F2B3]">Your infrastructure should too.</span>
          </h2>
        </Reveal>
        <ul className="divide-y divide-white/[0.06] border-y border-white/[0.06]">
          {LONG.map(([Icon, t], i) => (
            <Reveal as="li" key={t} delay={i * 70} className="long-item group flex items-center gap-5 py-5">
              <Icon className="h-5 w-5 text-[#67F2B3]/70 group-hover:text-[#67F2B3] transition-colors shrink-0" strokeWidth={1.5} />
              <span className="text-lg md:text-xl text-[#F4FFF9]/80 group-hover:text-[#F4FFF9] transition-colors">{t}</span>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}
